import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { CloudOff, CheckCircle2, RotateCw } from 'lucide-react';

import { useAuthStore } from '@/stores/authStore';
import { useWsStatus } from '@/hooks/useWsStatus';
import { wsClient } from '@/lib/wsClient';
import { cn } from '@/lib/utils';

const SHOW_AFTER_MS = 3_000;       // grace period to avoid flicker on transient blips
const BACK_ONLINE_MS = 2_500;       // how long the "back online" confirmation lingers

/**
 * YouTube-style full-width status bar pinned to the top of the viewport.
 * Appears after a few seconds of being offline (or on immediate network loss),
 * shows a brief "Back online" confirmation when connectivity returns,
 * and offers a manual Reconnect button.
 */
export function OfflineBar() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const wsStatus = useWsStatus();
  const isAuth = useAuthStore((s) => s.status === 'authenticated');
  const [networkOnline, setNetworkOnline] = useState<boolean>(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [show, setShow] = useState(false);
  const [showBackOnline, setShowBackOnline] = useState(false);
  const wasOfflineRef = useRef(false);

  // Track navigator.onLine
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onOnline = () => setNetworkOnline(true);
    const onOffline = () => setNetworkOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const isOffline = !networkOnline || wsStatus === 'offline' || wsStatus === 'reconnecting';

  // Delay showing the bar to skip transient blips, but show immediately if the
  // browser itself reports offline (those are real disconnects, not WS jitter).
  useEffect(() => {
    if (!isOffline) {
      setShow(false);
      return;
    }
    if (!networkOnline) {
      setShow(true);
      return;
    }
    const t0 = window.setTimeout(() => setShow(true), SHOW_AFTER_MS);
    return () => window.clearTimeout(t0);
  }, [isOffline, networkOnline]);

  // "Back online" pulse when going from offline → online
  useEffect(() => {
    if (isOffline) {
      wasOfflineRef.current = true;
      setShowBackOnline(false);
    } else if (wasOfflineRef.current) {
      wasOfflineRef.current = false;
      setShowBackOnline(true);
      const t0 = window.setTimeout(() => setShowBackOnline(false), BACK_ONLINE_MS);
      return () => window.clearTimeout(t0);
    }
  }, [isOffline]);

  if (!isAuth) return null;
  if (typeof document === 'undefined') return null;
  if (!show && !showBackOnline) return null;

  const offlineMode = show; // takes precedence over the back-online pulse
  const handleReconnect = () => {
    wsClient.connect();
    qc.invalidateQueries({ queryKey: ['orders'] });
    qc.invalidateQueries({ queryKey: ['me'] });
  };

  const message = offlineMode
    ? !networkOnline
      ? t('offline.noInternet')
      : wsStatus === 'reconnecting'
        ? t('offline.reconnecting')
        : t('offline.disconnected')
    : t('offline.backOnline');

  return createPortal(
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        'fixed inset-x-0 top-0 z-[65] flex items-center justify-center gap-3 px-4 py-2 text-sm shadow-[0_4px_12px_-4px_rgba(0,0,0,0.25)] animate-slide-in-down',
        offlineMode ? 'bg-foreground text-background' : 'bg-success text-success-foreground',
      )}
    >
      {offlineMode ? (
        <CloudOff className="h-4 w-4 shrink-0" />
      ) : (
        <CheckCircle2 className="h-4 w-4 shrink-0" />
      )}
      <span className="font-medium">{message}</span>
      {offlineMode && (
        <button
          type="button"
          onClick={handleReconnect}
          className="ml-1 inline-flex items-center gap-1 rounded-full border border-background/30 bg-background/10 px-3 py-0.5 text-xs font-semibold uppercase tracking-[0.18em] transition-colors hover:bg-background/20"
        >
          <RotateCw className="h-3 w-3" />
          {t('offline.reconnect')}
        </button>
      )}
    </div>,
    document.body,
  );
}
